/**
 * Admin token usage dashboard.
 *
 * Displays AI token consumption across admin and student workflows: summary totals,
 * usage grouped by user, usage grouped by task/feature/model, and a per-user task
 * breakdown drilldown. Supports a date range and provider filter that refresh all
 * sections together. All data loading is scoped to this dashboard so failures do
 * not affect other admin sections.
 *
 * @module TokenUsageManager
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Coins, Hash, Loader2, RefreshCw } from 'lucide-react';
import {
  getTokenUsageByTask,
  getTokenUsageByUser,
  getTokenUsageSummary,
  getTokenUsageUserDetail,
  type TokenUsageByTask,
  type TokenUsageByUser,
  type TokenUsageSummary,
  type TokenUsageUserDetail,
} from '@/lib/tokenUsageApi';

const PROVIDER_OPTIONS = [
  { value: 'all', label: 'All providers' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'anthropic', label: 'Anthropic' },
];

const formatNumber = (value: number): string => value.toLocaleString();

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
};

const formatAudioDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const min = Math.floor(seconds / 60);
  const sec = Math.round(seconds % 60);
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
};

interface Filters {
  startDate: string;
  endDate: string;
  provider: string;
}

const toApiFilters = (filters: Filters) => {
  const apiFilters: Record<string, string> = {};
  if (filters.startDate) apiFilters.startDate = `${filters.startDate}T00:00:00Z`;
  if (filters.endDate) apiFilters.endDate = `${filters.endDate}T23:59:59Z`;
  if (filters.provider && filters.provider !== 'all') apiFilters.provider = filters.provider;
  return apiFilters;
};

export default function TokenUsageManager() {
  const [filters, setFilters] = useState<Filters>({
    startDate: '',
    endDate: '',
    provider: 'all',
  });
  const [appliedFilters, setAppliedFilters] = useState<Filters>(filters);

  const [summary, setSummary] = useState<TokenUsageSummary | null>(null);
  const [byUser, setByUser] = useState<TokenUsageByUser | null>(null);
  const [byTask, setByTask] = useState<TokenUsageByTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<TokenUsageUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const loadDashboard = useCallback(async (active: Filters) => {
    setLoading(true);
    setError(null);
    try {
      const apiFilters = toApiFilters(active);
      const [summaryData, userData, taskData] = await Promise.all([
        getTokenUsageSummary(apiFilters),
        getTokenUsageByUser(apiFilters),
        getTokenUsageByTask(apiFilters),
      ]);
      setSummary(summaryData);
      setByUser(userData);
      setByTask(taskData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load token usage data');
      setSummary(null);
      setByUser(null);
      setByTask(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard(appliedFilters);
  }, [appliedFilters, loadDashboard]);

  const loadUserDetail = useCallback(
    async (userId: string, active: Filters) => {
      setSelectedUserId(userId);
      setDetailLoading(true);
      setDetailError(null);
      try {
        const detail = await getTokenUsageUserDetail(userId, toApiFilters(active));
        setUserDetail(detail);
      } catch (err) {
        setDetailError(
          err instanceof Error ? err.message : 'Failed to load user token usage detail',
        );
        setUserDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [],
  );

  const handleApply = () => {
    setSelectedUserId(null);
    setUserDetail(null);
    setAppliedFilters({ ...filters });
  };

  const hasNoUsage =
    !loading &&
    !error &&
    summary !== null &&
    summary.eventCount === 0 &&
    (byUser?.users.length ?? 0) === 0 &&
    (byTask?.tasks.length ?? 0) === 0;

  // Aggregate per-model totals from the already-loaded byTask data.
  const modelBreakdown = useMemo(() => {
    const acc: Record<string, { totalTokens: number; promptTokens: number; completionTokens: number; eventCount: number; audioSeconds: number }> = {};
    for (const row of byTask?.tasks ?? []) {
      const key = row.model ?? 'unknown';
      if (!acc[key]) acc[key] = { totalTokens: 0, promptTokens: 0, completionTokens: 0, eventCount: 0, audioSeconds: 0 };
      acc[key].totalTokens += row.totalTokens;
      acc[key].promptTokens += row.promptTokens;
      acc[key].completionTokens += row.completionTokens;
      acc[key].eventCount += row.eventCount;
      acc[key].audioSeconds += row.audioSeconds ?? 0;
    }
    return Object.entries(acc).sort((a, b) => b[1].totalTokens - a[1].totalTokens);
  }, [byTask]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground font-ui">Token Usage</h2>
        <p className="text-muted-foreground mt-1 font-ui">
          AI token consumption for admin and student workflows. Tracking starts from
          rollout; usage before then is not available.
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            <div className="flex-1">
              <Label htmlFor="token-usage-start" className="font-ui">
                Start date
              </Label>
              <Input
                id="token-usage-start"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="token-usage-end" className="font-ui">
                End date
              </Label>
              <Input
                id="token-usage-end"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
              />
            </div>
            <div className="flex-1">
              <Label className="font-ui">Provider</Label>
              <Select
                value={filters.provider}
                onValueChange={(value) => setFilters((f) => ({ ...f, provider: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All providers" />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleApply} disabled={loading} className="gap-2">
              <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
              Apply
            </Button>
          </div>
          {summary?.range?.startDate && (
            <p className="text-xs text-muted-foreground mt-3 font-ui">
              Showing {formatDate(summary.range.startDate)} – {formatDate(summary.range.endDate)}
            </p>
          )}
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span className="font-ui">Loading token usage…</span>
        </div>
      )}

      {error && !loading && (
        <Card className="border-destructive">
          <CardContent className="p-6 flex items-center gap-3 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <div className="flex-1 font-ui">{error}</div>
            <Button variant="outline" size="sm" onClick={() => loadDashboard(appliedFilters)}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {hasNoUsage && (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground font-ui">
            No token usage found for the selected range. Token tracking starts from
            rollout — try widening the date range.
          </CardContent>
        </Card>
      )}

      {!loading && !error && summary && !hasNoUsage && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Total tokens with input/output breakdown */}
            <Card className="sm:col-span-2">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="font-ui text-sm text-muted-foreground">Total tokens</p>
                    <p className="font-ui text-2xl font-bold text-primary">
                      {formatNumber(summary.totalTokens)}
                    </p>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300">
                        ↑ Input&nbsp;
                        <span className="font-semibold">{formatNumber(summary.promptTokens)}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                        ↓ Output&nbsp;
                        <span className="font-semibold">{formatNumber(summary.completionTokens)}</span>
                      </span>
                    </div>
                  </div>
                  <Coins className="h-8 w-8 text-primary shrink-0" />
                </div>
              </CardContent>
            </Card>

            {/* Events */}
            <SummaryCard
              label="Events"
              value={formatNumber(summary.eventCount)}
              icon={<Hash className="h-8 w-8 text-primary" />}
            />
          </div>

          {/* By-model breakdown */}
          {modelBreakdown.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <p className="font-ui text-sm font-medium text-muted-foreground mb-3">By model</p>
                <div className="flex flex-wrap gap-3">
                  {modelBreakdown.map(([model, stats]) => {
                    const isAudioOnly = stats.totalTokens === 0 && stats.audioSeconds > 0;
                    return (
                      <div
                        key={model}
                        className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-4 py-3 min-w-[180px]"
                      >
                        <code className="text-xs font-mono font-semibold text-foreground">
                          {model}
                        </code>
                        {isAudioOnly ? (
                          <>
                            <p className="text-lg font-bold text-primary font-ui">
                              {formatAudioDuration(stats.audioSeconds)}
                              <span className="text-xs font-normal text-muted-foreground ml-1">audio</span>
                            </p>
                            <div className="text-xs text-muted-foreground font-ui">
                              {formatNumber(stats.eventCount)} events · billed by duration
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-lg font-bold text-primary font-ui">
                              {formatNumber(stats.totalTokens)}
                              <span className="text-xs font-normal text-muted-foreground ml-1">tokens</span>
                            </p>
                            <div className="flex gap-2 text-xs text-muted-foreground font-ui">
                              <span className="text-blue-600 dark:text-blue-400">
                                ↑ {formatNumber(stats.promptTokens)}
                              </span>
                              <span className="text-emerald-600 dark:text-emerald-400">
                                ↓ {formatNumber(stats.completionTokens)}
                              </span>
                              <span className="ml-auto">{formatNumber(stats.eventCount)} events</span>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Usage by user */}
          <Card>
            <CardHeader>
              <CardTitle className="font-ui text-lg">Usage by user</CardTitle>
            </CardHeader>
            <CardContent>
              {byUser && byUser.users.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Prompt</TableHead>
                      <TableHead className="text-right">Completion</TableHead>
                      <TableHead className="text-right">Events</TableHead>
                      <TableHead className="text-right">Last used</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byUser.users.map((row) => (
                      <TableRow
                        key={row.userId ?? 'unattributed'}
                        className={row.userId ? 'cursor-pointer' : ''}
                        onClick={() =>
                          row.userId ? loadUserDetail(row.userId, appliedFilters) : undefined
                        }
                      >
                        <TableCell className="font-medium">
                          {row.userLabel}
                          {selectedUserId && selectedUserId === row.userId && (
                            <Badge variant="secondary" className="ml-2">
                              Selected
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(row.totalTokens)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.promptTokens)}</TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.completionTokens)}
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(row.eventCount)}</TableCell>
                        <TableCell className="text-right">{formatDate(row.lastUsedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground font-ui">No per-user usage.</p>
              )}
            </CardContent>
          </Card>

          {/* Per-user task breakdown */}
          {selectedUserId && (
            <Card>
              <CardHeader>
                <CardTitle className="font-ui text-lg">
                  Task breakdown{userDetail ? ` — ${userDetail.userLabel}` : ''}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailLoading && (
                  <div className="flex items-center text-muted-foreground py-4">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    <span className="font-ui">Loading breakdown…</span>
                  </div>
                )}
                {detailError && !detailLoading && (
                  <div className="flex items-center gap-2 text-destructive font-ui py-2">
                    <AlertCircle className="h-4 w-4" />
                    {detailError}
                  </div>
                )}
                {!detailLoading && !detailError && userDetail && (
                  <TaskTable rows={userDetail.tasks} emptyText="No tracked tasks for this user." />
                )}
              </CardContent>
            </Card>
          )}

          {/* Usage by task */}
          <Card>
            <CardHeader>
              <CardTitle className="font-ui text-lg">Usage by task</CardTitle>
            </CardHeader>
            <CardContent>
              <TaskTable rows={byTask?.tasks ?? []} emptyText="No per-task usage." />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-ui text-sm text-muted-foreground">{label}</p>
            <p className="font-ui text-2xl font-bold text-primary">{value}</p>
          </div>
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}

function TaskTable({
  rows,
  emptyText,
}: {
  rows: Array<{
    task: string;
    feature: string;
    model: string | null;
    totalTokens: number;
    promptTokens: number;
    completionTokens: number;
    eventCount: number;
    lastUsedAt: string | null;
    audioSeconds: number | null;
  }>;
  emptyText: string;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground font-ui">{emptyText}</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Task</TableHead>
          <TableHead>Feature</TableHead>
          <TableHead>Model</TableHead>
          <TableHead className="text-right">Total</TableHead>
          <TableHead className="text-right">Prompt</TableHead>
          <TableHead className="text-right">Completion</TableHead>
          <TableHead className="text-right">Audio</TableHead>
          <TableHead className="text-right">Events</TableHead>
          <TableHead className="text-right">Last used</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.task}:${row.feature}:${row.model ?? ''}`}>
            <TableCell className="font-medium">{row.task}</TableCell>
            <TableCell>{row.feature}</TableCell>
            <TableCell>
              {row.model ? (
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                  {row.model}
                </code>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="text-right">{formatNumber(row.totalTokens)}</TableCell>
            <TableCell className="text-right">{formatNumber(row.promptTokens)}</TableCell>
            <TableCell className="text-right">{formatNumber(row.completionTokens)}</TableCell>
            <TableCell className="text-right">
              {row.audioSeconds ? (
                <span className="text-amber-600 dark:text-amber-400 font-medium">
                  {formatAudioDuration(row.audioSeconds)}
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="text-right">{formatNumber(row.eventCount)}</TableCell>
            <TableCell className="text-right">{formatDate(row.lastUsedAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
